import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { RosterListItem } from "./RosterGuidePanel";
import {
  sortByName,
  sortThemesMajorFirst,
  type BookMotif,
  type BookSymbol,
  type BookTheme,
  type MotifRemovalRecord,
  type SymbolRemovalRecord,
  type ThemeKind,
  type ThemeRemovalRecord,
  type ThemeType,
} from "../../../shared/themeRoster";

type GuideKindFilter = "all" | ThemeKind;

type SelectedRef = { kind: ThemeKind; id: string } | null;

export function ThemesGuidePanel({
  themes,
  motifs,
  symbols,
  themeRemovals,
  motifRemovals,
  symbolRemovals,
  chapters,
  selected,
  onSelect,
  onUpdateTheme,
  onUpdateMotif,
  onUpdateSymbol,
  onRequestRemoveTheme,
  onRequestRemoveMotif,
  onRequestRemoveSymbol,
  onAdd,
  onAnalyzeWholeBook,
  onOpenChapter,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  busy,
}: {
  themes: BookTheme[];
  motifs: BookMotif[];
  symbols: BookSymbol[];
  themeRemovals: ThemeRemovalRecord[];
  motifRemovals: MotifRemovalRecord[];
  symbolRemovals: SymbolRemovalRecord[];
  chapters: Array<{ id: string; order: number; title: string }>;
  selected: SelectedRef;
  onSelect: (next: SelectedRef) => void;
  onUpdateTheme: (id: string, patch: Partial<BookTheme>) => void;
  onUpdateMotif: (id: string, patch: Partial<BookMotif>) => void;
  onUpdateSymbol: (id: string, patch: Partial<BookSymbol>) => void;
  onRequestRemoveTheme: (theme: BookTheme) => void;
  onRequestRemoveMotif: (motif: BookMotif) => void;
  onRequestRemoveSymbol: (symbol: BookSymbol) => void;
  onAdd: (kind: ThemeKind) => void;
  onAnalyzeWholeBook: () => void;
  onOpenChapter: (chapterId: string) => void;
  extractModel: string;
  onExtractModelChange: (
    model: string,
    provider: "openai" | "anthropic",
  ) => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  busy: boolean;
}) {
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<GuideKindFilter>("all");

  const chapterTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const ch of chapters) map.set(ch.id, ch.title);
    return map;
  }, [chapters]);

  const filteredThemes = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortThemesMajorFirst(themes).filter((t) => {
      if (kindFilter !== "all" && kindFilter !== "theme") return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.evolutionNotes.toLowerCase().includes(q)
      );
    });
  }, [themes, query, kindFilter]);

  const filteredMotifs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortByName(motifs).filter((m) => {
      if (kindFilter !== "all" && kindFilter !== "motif") return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q)
      );
    });
  }, [motifs, query, kindFilter]);

  const filteredSymbols = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortByName(symbols).filter((s) => {
      if (kindFilter !== "all" && kindFilter !== "symbol") return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) || s.meaning.toLowerCase().includes(q)
      );
    });
  }, [symbols, query, kindFilter]);

  const selectedTheme =
    selected?.kind === "theme"
      ? (themes.find((t) => t.id === selected.id) ?? null)
      : null;
  const selectedMotif =
    selected?.kind === "motif"
      ? (motifs.find((m) => m.id === selected.id) ?? null)
      : null;
  const selectedSymbol =
    selected?.kind === "symbol"
      ? (symbols.find((s) => s.id === selected.id) ?? null)
      : null;

  useEffect(() => {
    if (selected) return;
    if (filteredThemes[0]) {
      onSelect({ kind: "theme", id: filteredThemes[0].id });
      return;
    }
    if (filteredMotifs[0]) {
      onSelect({ kind: "motif", id: filteredMotifs[0].id });
      return;
    }
    if (filteredSymbols[0]) {
      onSelect({ kind: "symbol", id: filteredSymbols[0].id });
    }
  }, [selected, filteredThemes, filteredMotifs, filteredSymbols, onSelect]);

  const removalCount =
    themeRemovals.length + motifRemovals.length + symbolRemovals.length;

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden" aria-label="Themes guide">
      <Card className="m-1.5 h-full min-h-0 flex-1 gap-0 rounded-lg border border-border bg-card py-0 shadow-sm ring-0">
        <PanelHeader
          title="Themes & Motifs"
          description={`Book-level themes, motifs, and symbols with chapter appearances.${
            removalCount > 0
              ? ` ${removalCount} removal reason(s) remembered.`
              : ""
          }`}
          actions={
            <>
              <ExtractToolbar
                model={extractModel}
                onModelChange={onExtractModelChange}
                openAiModels={openAiModels}
                anthropicModels={anthropicModels}
                onExtract={onAnalyzeWholeBook}
                extractBusy={busy}
                extractLabel="Analyze whole book"
                extractBusyLabel="Analyzing…"
              />
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => onAdd("theme")}
              >
                Add theme
              </Button>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => onAdd("motif")}
              >
                Add motif
              </Button>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => onAdd("symbol")}
              >
                Add symbol
              </Button>
            </>
          }
        />

        <div className="grid min-h-0 flex-1 grid-cols-1 border-t border-border min-[901px]:grid-cols-[minmax(200px,280px)_1fr]">
          <aside
            className="max-h-55 overflow-auto border-b border-border bg-muted/10 min-[901px]:max-h-none min-[901px]:border-r min-[901px]:border-b-0"
            aria-label="Themes motifs symbols list"
          >
            <div className="p-3 border-b border-border">
              <Input
                aria-label="Search themes motifs symbols"
                placeholder="Search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="flex flex-wrap gap-1 mt-2">
                {(["all", "theme", "motif", "symbol"] as const).map((kind) => (
                  <Button
                    key={kind}
                    size="sm"
                    variant={kindFilter === kind ? "default" : "secondary"}
                    type="button"
                    onClick={() => setKindFilter(kind)}
                  >
                    {kind === "all"
                      ? "All"
                      : kind.charAt(0).toUpperCase() + kind.slice(1) + "s"}
                  </Button>
                ))}
              </div>
            </div>
            {filteredThemes.length + filteredMotifs.length + filteredSymbols.length === 0 ? (
              <EmptyState
                compact
                className="m-3"
                title="No matching entries."
                description="Try another search or filter."
              />
            ) : (
              <ul className="m-0 list-none p-0">
                {filteredThemes.map((theme) => (
                  <li key={theme.id}>
                    <RosterListItem
                      active={
                        selected?.kind === "theme" && selected.id === theme.id
                      }
                      onClick={() => onSelect({ kind: "theme", id: theme.id })}
                    >
                      <span className="col-span-2 truncate text-[0.85rem] font-semibold">
                        {theme.name}
                      </span>
                      <span className="col-span-2 text-xs text-muted-foreground">
                        Theme · {theme.type} · {theme.appearances.length} ch
                      </span>
                    </RosterListItem>
                  </li>
                ))}
                {filteredMotifs.map((motif) => (
                  <li key={motif.id}>
                    <RosterListItem
                      active={
                        selected?.kind === "motif" && selected.id === motif.id
                      }
                      onClick={() => onSelect({ kind: "motif", id: motif.id })}
                    >
                      <span className="col-span-2 truncate text-[0.85rem] font-semibold">
                        {motif.name}
                      </span>
                      <span className="col-span-2 text-xs text-muted-foreground">
                        Motif · {motif.appearances.length} ch
                      </span>
                    </RosterListItem>
                  </li>
                ))}
                {filteredSymbols.map((symbol) => (
                  <li key={symbol.id}>
                    <RosterListItem
                      active={
                        selected?.kind === "symbol" && selected.id === symbol.id
                      }
                      onClick={() => onSelect({ kind: "symbol", id: symbol.id })}
                    >
                      <span className="col-span-2 truncate text-[0.85rem] font-semibold">
                        {symbol.name}
                      </span>
                      <span className="col-span-2 text-xs text-muted-foreground">
                        Symbol · {symbol.appearances.length} ch
                      </span>
                    </RosterListItem>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div
            className="min-h-0 overflow-auto px-[1.15rem] pb-6 pt-4"
            aria-label="Selected entry detail"
          >
            {selectedTheme ? (
              <ThemeDetail
                theme={selectedTheme}
                chapterTitle={chapterTitle}
                onUpdate={onUpdateTheme}
                onRequestRemove={onRequestRemoveTheme}
                onOpenChapter={onOpenChapter}
              />
            ) : selectedMotif ? (
              <MotifDetail
                motif={selectedMotif}
                chapterTitle={chapterTitle}
                onUpdate={onUpdateMotif}
                onRequestRemove={onRequestRemoveMotif}
                onOpenChapter={onOpenChapter}
              />
            ) : selectedSymbol ? (
              <SymbolDetail
                symbol={selectedSymbol}
                chapterTitle={chapterTitle}
                onUpdate={onUpdateSymbol}
                onRequestRemove={onRequestRemoveSymbol}
                onOpenChapter={onOpenChapter}
              />
            ) : (
              <EmptyState
                title="Select an entry"
                description="Choose a theme, motif, or symbol — or add one to get started."
              />
            )}
          </div>
        </div>
      </Card>
    </section>
  );
}

function ThemeDetail({
  theme,
  chapterTitle,
  onUpdate,
  onRequestRemove,
  onOpenChapter,
}: {
  theme: BookTheme;
  chapterTitle: Map<string, string>;
  onUpdate: (id: string, patch: Partial<BookTheme>) => void;
  onRequestRemove: (theme: BookTheme) => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Input
          aria-label="Theme name"
          value={theme.name}
          onChange={(e) => onUpdate(theme.id, { name: e.target.value })}
        />
        <div className="flex flex-wrap gap-2">
          <Select
            value={theme.type}
            onValueChange={(value) =>
              onUpdate(theme.id, { type: value as ThemeType })
            }
          >
            <SelectTrigger
              className="min-w-26"
              size="sm"
              aria-label="Theme type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="major">Major</SelectItem>
              <SelectItem value="minor">Minor</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="destructive"
            type="button"
            onClick={() => onRequestRemove(theme)}
          >
            Remove
          </Button>
        </div>
      </div>
      <Field label="Description">
        <Textarea
          aria-label="Theme description"
          value={theme.description}
          rows={3}
          onChange={(e) => onUpdate(theme.id, { description: e.target.value })}
        />
      </Field>
      <Field label="Evolution notes">
        <Textarea
          aria-label="Theme evolution"
          value={theme.evolutionNotes}
          rows={3}
          placeholder="How the theme develops across the book…"
          onChange={(e) =>
            onUpdate(theme.id, { evolutionNotes: e.target.value })
          }
        />
      </Field>
      <div>
        <h3 className="mb-2 text-sm font-medium">Chapter appearances</h3>
        {theme.appearances.length === 0 ? (
          <EmptyState compact title="No chapter appearances yet." />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {theme.appearances.map((a) => (
              <li key={a.chapterId}>
                <Card
                  size="sm"
                  className="gap-1.5 rounded-md p-3 py-3 ring-1 ring-border"
                >
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto w-fit justify-start p-0"
                    onClick={() => onOpenChapter(a.chapterId)}
                  >
                    {chapterTitle.get(a.chapterId) ?? a.chapterId}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {a.manifestation || "No manifestation note"}
                  </p>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MotifDetail({
  motif,
  chapterTitle,
  onUpdate,
  onRequestRemove,
  onOpenChapter,
}: {
  motif: BookMotif;
  chapterTitle: Map<string, string>;
  onUpdate: (id: string, patch: Partial<BookMotif>) => void;
  onRequestRemove: (motif: BookMotif) => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Input
          aria-label="Motif name"
          value={motif.name}
          onChange={(e) => onUpdate(motif.id, { name: e.target.value })}
        />
        <Button
          size="sm"
          variant="destructive"
          type="button"
          onClick={() => onRequestRemove(motif)}
        >
          Remove
        </Button>
      </div>
      <Field label="Description">
        <Textarea
          aria-label="Motif description"
          value={motif.description}
          rows={3}
          onChange={(e) => onUpdate(motif.id, { description: e.target.value })}
        />
      </Field>
      <div>
        <h3 className="mb-2 text-sm font-medium">Chapter appearances</h3>
        {motif.appearances.length === 0 ? (
          <EmptyState compact title="No chapter appearances yet." />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {motif.appearances.map((a) => (
              <li key={a.chapterId}>
                <Card
                  size="sm"
                  className="gap-1.5 rounded-md p-3 py-3 ring-1 ring-border"
                >
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto w-fit justify-start p-0"
                    onClick={() => onOpenChapter(a.chapterId)}
                  >
                    {chapterTitle.get(a.chapterId) ?? a.chapterId}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {a.context || "No context note"}
                  </p>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SymbolDetail({
  symbol,
  chapterTitle,
  onUpdate,
  onRequestRemove,
  onOpenChapter,
}: {
  symbol: BookSymbol;
  chapterTitle: Map<string, string>;
  onUpdate: (id: string, patch: Partial<BookSymbol>) => void;
  onRequestRemove: (symbol: BookSymbol) => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Input
          aria-label="Symbol name"
          value={symbol.name}
          onChange={(e) => onUpdate(symbol.id, { name: e.target.value })}
        />
        <Button
          size="sm"
          variant="destructive"
          type="button"
          onClick={() => onRequestRemove(symbol)}
        >
          Remove
        </Button>
      </div>
      <Field label="Meaning">
        <Textarea
          aria-label="Symbol meaning"
          value={symbol.meaning}
          rows={3}
          onChange={(e) => onUpdate(symbol.id, { meaning: e.target.value })}
        />
      </Field>
      <div>
        <h3 className="mb-2 text-sm font-medium">Chapter appearances</h3>
        {symbol.appearances.length === 0 ? (
          <EmptyState compact title="No chapter appearances yet." />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {symbol.appearances.map((a) => (
              <li key={a.chapterId}>
                <Card
                  size="sm"
                  className="gap-1.5 rounded-md p-3 py-3 ring-1 ring-border"
                >
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto w-fit justify-start p-0"
                    onClick={() => onOpenChapter(a.chapterId)}
                  >
                    {chapterTitle.get(a.chapterId) ?? a.chapterId}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {a.context || "No context note"}
                  </p>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
